import type { Body as BodyNode } from "../../types/scene";

export type ExportBodyNode = Omit<BodyNode, "children"> & { children?: ExportBodyNode[] };

export const collectMissingBodyChildclassDefaults = ({
  nodes,
  bodyChildclassDefaultNames,
  bodyChildclassStubNames,
}: {
  nodes: ExportBodyNode[];
  bodyChildclassDefaultNames: Set<string>;
  bodyChildclassStubNames: Set<string>;
}): void => {
  nodes.forEach((node) => {
    const childclass = typeof node.childclass === "string" ? node.childclass.trim() : "";
    if (childclass && !bodyChildclassDefaultNames.has(childclass)) {
      bodyChildclassDefaultNames.add(childclass);
      bodyChildclassStubNames.add(childclass);
    }
    if (Array.isArray(node.children) && node.children.length > 0) {
      collectMissingBodyChildclassDefaults({
        nodes: node.children,
        bodyChildclassDefaultNames,
        bodyChildclassStubNames,
      });
    }
  });
};

export const flattenBodyTree = (nodes: ExportBodyNode[]): ExportBodyNode[] => {
  const out: ExportBodyNode[] = [];
  const walk = (node: ExportBodyNode) => {
    if (!node) return;
    out.push(node);
    (node.children || []).forEach(walk);
  };
  nodes.forEach(walk);
  return out;
};

export const collectMeshUuids = (nodes: ExportBodyNode[]): string[] => {
  const set = new Set<string>();
  const walk = (node: ExportBodyNode): void => {
    node.geoms?.forEach((geom) => {
      if (geom.mesh) set.add(geom.mesh);
    });
    node.children?.forEach((child) => walk(child));
  };
  nodes.forEach(walk);
  return Array.from(set);
};

export const collectHFieldNames = (nodes: ExportBodyNode[]): string[] => {
  const set = new Set<string>();
  const walk = (node: ExportBodyNode) => {
    if (!node) return;
    (node.geoms || []).forEach((geom) => {
      if (geom?.kind === "hfield" && geom.heightField) set.add(String(geom.heightField));
    });
    node.children?.forEach((child) => walk(child));
  };
  nodes.forEach(walk);
  return Array.from(set);
};

export const collectBodyNames = (
  nodes: ExportBodyNode[],
  acc: Set<string> = new Set<string>()
): Set<string> => {
  nodes.forEach((node) => {
    acc.add(node.name);
    if (node.children?.length) {
      collectBodyNames(node.children, acc);
    }
  });
  return acc;
};
